import { useState } from "react";
import { Button } from "../components/ui/Button";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { updateClientTermsAcceptance, updateClientPurchaseType } from "../supabase";
import { Users, User } from "lucide-react";

const TermsAcceptance = () => {
  const [accepted, setAccepted] = useState(false);
  const [purchaseType, setPurchaseType] = useState(null); // 'solo' o 'pareja'
  const [isLoading, setIsLoading] = useState(false);
  const { user, setTermsAccepted } = useAuth();
  const navigate = useNavigate();

  const handleAccept = async () => {
    if (!accepted || !purchaseType) return;
    
    setIsLoading(true);
    try {
      // Actualizar términos y tipo de compra al mismo tiempo
      await updateClientTermsAcceptance(user.email, true);
      await updateClientPurchaseType(user.email, purchaseType);
      // Actualizar el estado en el contexto
      setTermsAccepted(true);
      // Redirigir directamente al chatbot
      navigate("/chat", { replace: true });
    } catch (error) {
      // Mostrar mensaje de error más descriptivo
      let errorMessage = "Error al guardar la aceptación. Inténtalo de nuevo.";
      
      if (error.message && error.message.includes('no están configurados')) {
        errorMessage = "Error: Los campos de términos no están configurados en la base de datos. Por favor, contacta al administrador o ejecuta el script SQL necesario.";
      } else if (error.message && error.message.includes('no encontrado')) {
        errorMessage = "Error: Usuario no encontrado. Por favor, inicia sesión nuevamente.";
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <div className="container mx-auto px-4 pt-24 pb-8 max-w-4xl">
        <div className="glass rounded-3xl border border-primary/20 p-8 shadow-2xl">
          <h1 className="text-3xl font-bold mb-6 text-center">
            Política de Privacidad
          </h1>

          <div className="max-h-[60vh] overflow-y-auto mb-6 pr-4 space-y-4 text-sm leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold mb-3">Derecho de información</h2>
              <p className="mb-4">
                A los efectos de lo dispuesto en el Reglamento (UE) 2016/679 de 27 de abril de 2016 (GDPR), relativo a la protección de las personas físicas en lo que respecta al tratamiento de datos personales y a la libre circulación de estos datos y a la Ley Orgánica 3/2018 de 5 de diciembre, de protección de datos de carácter personal y garantía de los derechos digitales (LOPDGDD), se informa al usuario de la existencia de un tratamiento automatizado de datos de carácter personal creado por HABILITANDO TU CASA SL y bajo su responsabilidad con los datos personales que nos pudiera facilitar durante la navegación o al utilizar la aplicación GABI IA ya bien sea en el momento de su registro o cuando remita un mensaje de correo electrónico.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Responsable de tratamiento:</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Denominación social:</strong> HABILITANDO TU CASA SL</li>
                <li><strong>Nombre comercial:</strong> HABILY</li>
                <li><strong>CIF:</strong> B21832969</li>
                <li><strong>Domicilio social:</strong> AVDA REPUBLICA ARGENTINA 25 9 PLANTA, 41011 - SEVILLA (SEVILLA)</li>
                <li><strong>Teléfono:</strong> 685133327</li>
                <li><strong>Correo electrónico a efectos de comunicación:</strong> lopd@habily.es</li>
                <li><strong>Datos de contacto Delegado de Protección de Datos:</strong> Teléfono: 984283532 Correo electrónico: delegadoprotecciondatos@prodasva.org</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Finalidad del tratamiento:</h2>
              <p className="mb-2">Le indicamos que la finalidad del tratamiento de sus datos es la descrita a continuación:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Formulario de contacto:</strong> los datos personales que nos facilite a través de dicho formulario serán tratados con la finalidad de gestionar su solicitud y enviarle información más detallada.</li>
                <li><strong>Formulario de registro GABI IA:</strong> mediante el "Continuar con Google", obtenemos un identificador único de usuario de Google y la dirección de correo electrónico asociada a su cuenta.</li>
                <li><strong>Datos facilitados al interactuar con GABI IA:</strong> Los datos proporcionados serán tratados con la finalidad de facilitar la compra de su vivienda, así como para ofrecer asesoramiento inmobiliario y financiero personalizado. Dicho tratamiento incluye: Análisis automatizado de anuncios y documentos, evaluación de riesgos y comparativas personalizadas, generación de informes PDF con datos personales y financieros, recomendaciones financieras personalizadas mediante IA.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Legitimación:</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Artículo 6.1.a) del RGPD. El interesado dio su consentimiento para el tratamiento de sus datos personales para uno o varios fines específicos.</li>
                <li>Artículo 6.1.b) del RGPD. Tratamiento necesario para la ejecución de un contrato en el que el interesado es parte o para la aplicación a petición de éste de medidas precontractuales.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Conservación y tratamiento de los datos:</h2>
              <p className="mb-2">
                Los datos proporcionados se conservarán durante el tiempo necesario para la finalidad en base a la cual han sido recabados, mientras el usuario mantenga su cuenta activa y utilice los servicios de GABI IA o para el cumplimiento con las obligaciones legales y atender las posibles responsabilidades que pudieran derivar del cumplimiento de la finalidad para la que los datos fueron recabados, los datos no serán cedidos a terceros salvo en los casos en que exista una obligación legal o consentimiento del interesado. En caso de ser necesario, HABILITANDO TU CASA SL cancelará o rectificará los datos cuando resulten inexactos, incompletos o hayan dejado de ser necesarios para su finalidad, de conformidad con lo previsto en la legislación vigente en materia de Protección de Datos de Carácter Personal.
              </p>
              <p className="mb-2">
                Mientras no nos comunique lo contrario, entenderemos que sus datos no han sido modificados, que usted se compromete a notificarnos cualquier variación y que tenemos el consentimiento para utilizarlos a fin de poder fidelizar la relación entre las partes.
              </p>
              <p className="mb-2">
                En caso de financiación a través de terceros, le informamos que sus datos podrán ser cedidos —siempre con su consentimiento previo, expreso e informado— a las entidades financieras o fondos con los que se tramite o se realice el estudio para la concesión de préstamos, hipotecas u otros servicios necesarios para la gestión solicitada.
              </p>
              <p className="mb-2">
                GABI IA realiza análisis automatizados y elaboración de perfiles financieros con base en los datos facilitados por el usuario, con el fin de ofrecer simulaciones hipotecarias, detectar riesgos inmobiliarios y proponer opciones de financiación o alquiler con opción a compra. Estas decisiones no tienen efectos jurídicos vinculantes, pero pueden influir significativamente en la toma de decisiones del usuario.
              </p>
              <p className="mb-2">
                El usuario podrá solicitar en cualquier momento la intervención humana, expresar su punto de vista u oponerse a cualquier decisión basada en dicho tratamiento automatizado contactando a lopd@habily.es.
              </p>
              <p className="mb-2">
                De acuerdo con lo que establece la Ley 34/2002, de Servicios de la Sociedad de la Información y el Comercio Electrónico en su artículo 21, solicitaremos su consentimiento para poder realizar comunicaciones publicitarias que consideremos puedan ser de su interés, por correo electrónico o por cualquier otro medio de comunicación electrónica equivalente.
              </p>
              <p className="mb-2">
                Usted tiene derecho a oponerse y/o retirar el consentimiento al tratamiento de los datos indicados anteriormente, en su caso, y en cualquier momento, sin que ello afecte a la licitud del tratamiento basado en el consentimiento previo a su retirada, mediante notificación al responsable del tratamiento en la/s dirección/es indicada/s.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Medidas técnicas y organizativas</h2>
              <p className="mb-2">
                HABILITANDO TU CASA SL adopta las medidas técnicas, controles y procedimientos de carácter físico, organizativo y tecnológico, razonablemente fiables y efectivos, orientados a preservar la integridad y la seguridad de sus datos y garantizar su privacidad. No obstante, no asume ninguna responsabilidad por los daños y perjuicios derivados de alteraciones que terceros puedan causar en los sistemas informáticos, documentos electrónicos o ficheros del usuario.
              </p>
              <p className="mb-2">
                Además, todo el personal con acceso a los datos personales ha sido formado y tiene conocimiento de sus obligaciones con relación a los tratamientos de datos personales.
              </p>
              <p className="mb-2">
                En el caso de los contratos que suscribimos con nuestros proveedores incluimos cláusulas en las que se les exige mantener el deber de secreto respecto a los datos de carácter personal a los que hayan tenido acceso en virtud del encargo realizado, así como implantar las medidas de seguridad técnicas y organizativas necesarias para garantizar la confidencialidad, integridad, disponibilidad y resiliencia permanentes de los sistemas y servicios de tratamiento de los datos personales.
              </p>
              <p className="mb-2">
                Todas estas medidas de seguridad son revisadas de forma periódica para garantizar su adecuación y efectividad.
              </p>
              <p className="mb-2">
                Sin embargo, la seguridad absoluta no se puede garantizar y no existe ningún sistema de seguridad que sea impenetrable por lo que, en el caso de cualquier información objeto de tratamiento y bajo nuestro control se viese comprometida como consecuencia de una brecha de seguridad, tomaremos las medidas adecuadas para investigar el incidente, notificarlo a la Autoridad de Control y, en su caso, a aquellos usuarios que se hubieran podido ver afectados para que tomen las medidas adecuadas.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Tratamiento mediante inteligencia artificial</h2>
              <p className="mb-2">
                GABI IA utiliza tecnologías de inteligencia artificial para analizar los datos e información que el usuario proporciona (por ejemplo, anuncios inmobiliarios, documentos o datos financieros) y generar informes, puntuaciones y recomendaciones personalizadas.
              </p>
              <p className="mb-2">
                Dichas recomendaciones son orientativas y no constituyen asesoramiento financiero vinculante ni decisiones con efectos jurídicos. El usuario puede solicitar información adicional sobre el funcionamiento general del sistema, intervención humana o eliminación de los datos tratados por la IA en cualquier momento escribiendo a lopd@habily.es.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">Derechos:</h2>
              <p className="mb-2">
                El usuario podrá ejercer los derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento, portabilidad de datos y a no ser objeto de decisiones individualizadas automatizadas, en relación con los datos objeto del tratamiento, ante el responsable del tratamiento en la dirección anteriormente mencionada o por correo electrónico a lopd@habily.es, adjuntando copia de su DNI o documento equivalente, o directamente ante el delegado de protección de datos. En caso de que no haya obtenido satisfacción en el ejercicio de sus derechos, podrá presentar una reclamación ante la Autoridad de Control en materia Protección de Datos competente, siendo ésta la Agencia Española de Protección de Datos, y cuyos datos de contacto están accesibles en{" "}
                <a 
                  href="https://sedeagpd.gob.es/sede-electronica-web/vistas/formNuevaReclamacion/reclamacion.jsf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  https://sedeagpd.gob.es/sede-electronica-web/vistas/formNuevaReclamacion/reclamacion.jsf
                </a>.
              </p>
            </section>
          </div>

          <div className="border-t border-border/50 pt-6 space-y-6">
            {/* Selección de tipo de compra */}
            <div>
              <h3 className="text-lg font-semibold mb-4 text-center">
                ¿Cómo planeas comprar tu vivienda?
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setPurchaseType('solo')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    purchaseType === 'solo'
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <User className={`w-8 h-8 ${purchaseType === 'solo' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className={`font-medium ${purchaseType === 'solo' ? 'text-primary' : 'text-foreground'}`}>
                      Solo
                    </span>
                    <span className="text-xs text-muted-foreground text-center">
                      Compraré la vivienda por mi cuenta
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setPurchaseType('pareja')}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    purchaseType === 'pareja'
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Users className={`w-8 h-8 ${purchaseType === 'pareja' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <span className={`font-medium ${purchaseType === 'pareja' ? 'text-primary' : 'text-foreground'}`}>
                      En pareja
                    </span>
                    <span className="text-xs text-muted-foreground text-center">
                      Compraré la vivienda con mi pareja
                    </span>
                  </div>
                </button>
              </div>
            </div>

            {/* Aceptación de términos */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-2 border-primary text-primary focus:ring-2 focus:ring-primary"
              />
              <span className="text-sm">
                He leído y acepto la <strong>Política de Privacidad</strong> y los <strong>Términos y Condiciones</strong> de HABILY. Entiendo que mis datos serán tratados conforme a lo establecido en esta política.
              </span>
            </label>
          </div>

          <div className="mt-6 flex justify-center">
            <Button
              onClick={handleAccept}
              variant="hero"
              size="lg"
              className="min-w-[200px]"
              disabled={!accepted || !purchaseType || isLoading}
            >
              {isLoading ? "Guardando..." : "Aceptar y continuar"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptance;

